import React, { ChangeEvent, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useProgressPolling } from '~/components/Accommodation/hooks/useProgressPolling';
import Spinner from '~/components/Common/Spinner';

import { AdminPrompt, ProgressStatusResponse, getProgressStatuses } from '~/services/AccommodationService';
import { HttpResponse } from '~/services/types';

import ProgressStateItem from '../Accommodation/Pages/Properties/components/ProgressStateItem';

export interface InitiateContentGenerationRequest {
  template: string;
  examples: Array<string>;
  variables: { abstain?: string; voice?: string };
  includeOfferInGeneration?: boolean;
  useWebSearch: boolean;
}

interface Props {
  onClose: () => void;
  prompt: AdminPrompt;
  setPrompt: (prompt: AdminPrompt) => void;
  initiateContentGeneration: (
    params: InitiateContentGenerationRequest,
  ) => Promise<HttpResponse<ProgressStatusResponse>>;
  fetchContent: () => void;
  includeOfferInGeneration?: boolean;
  setUseWebSearch?: (useWebSearch: boolean) => void;
  useWebSearch: boolean;
}

export default function AccommodationPropertyGenerateContentModal({
  onClose,
  prompt,
  setPrompt,
  initiateContentGeneration,
  fetchContent,
  includeOfferInGeneration,
  useWebSearch,
  setUseWebSearch,
}: Props) {
  const [isInitialConfirm, setIsInitialConfirm] = useState(true);
  const [newExample, setNewExample] = useState<string>('');

  const handleAddExample = () => {
    if (newExample.trim()) {
      setPrompt({ ...prompt, examples: [...(prompt.examples || []), newExample.trim()] });
      setNewExample('');
    }
  };

  const handleRemoveExample = (index: number) => {
    setPrompt({
      ...prompt,
      examples: (prompt.examples || []).filter((_, i) => i !== index),
    });
  };

  const handleExampleChange = (index: number, value: string) => {
    setPrompt({
      ...prompt,
      examples: (prompt.examples || []).map((example, i) => (i === index ? value : example)),
    });
  };

  const handlePromptChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPrompt({ ...prompt, template: e.target.value });
  };

  const handleNewExampleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewExample(e.target.value);
  };

  const handleAbstainChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPrompt({ ...prompt, variables: { ...prompt.variables, abstain: e.target.value } });
  };

  const handleVoiceChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPrompt({ ...prompt, variables: { ...prompt.variables, voice: e.target.value } });
  };

  const handleUseWebSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setUseWebSearch(e.target.checked);
  };

  const { isLoading, isComplete, statusMessage, errorMessage, statuses, runStartJob, resetAndClose } =
    useProgressPolling<ProgressStatusResponse>({
      apiCall: async () => {
        const response = await initiateContentGeneration({
          template: prompt.template,
          examples: prompt.examples,
          variables: {
            abstain: prompt.variables?.abstain as string,
            voice: prompt.variables?.voice as string,
          },
          includeOfferInGeneration,
          useWebSearch,
        });
        return {
          status: response.status,
          message: response.message,
          result: { id: response.result.id || '' },
        } as HttpResponse<{ id: string }>;
      },
      getStatus: getProgressStatuses,
      onClose,
    });

  const contentGeneration = statuses.find((status) => status.type === 'generatePropertyContent');

  const handleClose = () => {
    resetAndClose();
    onClose();
  };

  const fetchContentAndClose = () => {
    fetchContent();
    handleClose();
  };

  if (isInitialConfirm) {
    return (
      <Dialog open fullWidth maxWidth="lg">
        <DialogTitle>Generate Content</DialogTitle>
        <DialogContent>
          <Box my={2}>
            <Typography>
              This action will initiate the generation of base content. This is a long-running task that may take
              several minutes to complete. This view will refresh automatically as the job progresses.
            </Typography>
            <Stack spacing={2} mt={2}>
              <TextField
                label="Prompt"
                value={prompt.template}
                onChange={handlePromptChange}
                fullWidth
                multiline
                rows={6}
              />

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Examples
                </Typography>
                {(prompt.examples || []).map((example, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      value={example}
                      onChange={(e) => handleExampleChange(index, e.target.value)}
                      fullWidth
                      onBlur={() => setPrompt(prompt)}
                    />
                    <IconButton onClick={() => handleRemoveExample(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <TextField
                    value={newExample}
                    onChange={handleNewExampleChange}
                    placeholder="Add new example"
                    fullWidth
                  />
                  <IconButton onClick={handleAddExample} color="primary">
                    <AddIcon />
                  </IconButton>
                </Box>
              </Box>

              <TextField
                label="Abstain"
                value={prompt.variables?.abstain}
                onChange={handleAbstainChange}
                fullWidth
                multiline
                rows={3}
              />

              <TextField
                label="Voice"
                value={prompt.variables?.voice}
                onChange={handleVoiceChange}
                fullWidth
                multiline
                rows={1}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useWebSearch}
                    onChange={handleUseWebSearch}
                    inputProps={{ 'aria-label': 'Web Search Toggle' }}
                  />
                }
                label="Use Web Search (Beta)"
              />
            </Stack>
            <Typography mt={2}>Are you sure you want to proceed?</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="text">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setIsInitialConfirm(false);
              runStartJob();
            }}
            variant="contained"
          >
            Start Generation
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={handleClose}>
      <DialogTitle>Generating Content</DialogTitle>
      <DialogContent sx={{ minWidth: '400px' }}>
        <Typography mb={2}>
          This is a long-running task that may take several minutes to complete. This view will refresh automatically as
          the job progresses. Please do not close this view.
        </Typography>
        <Stack spacing={2}>
          {contentGeneration && <ProgressStateItem label={contentGeneration.message} state={contentGeneration.state} />}
          {isComplete && statusMessage && <DialogContentText color="primary">{statusMessage}</DialogContentText>}
          {errorMessage && <DialogContentText color="error">{errorMessage}</DialogContentText>}
        </Stack>
      </DialogContent>
      {!isComplete && (
        <DialogActions>
          <Button variant="text" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          {isLoading && (
            <Button variant="contained" disabled>
              <Spinner size={15} inline />
            </Button>
          )}
        </DialogActions>
      )}
      {isComplete && (
        <DialogActions>
          <Button variant="contained" onClick={fetchContentAndClose}>
            View Content
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
